import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { TASK_STATUS_LABELS } from '@epasu/core/constants';
import type { TaskStatus } from '@epasu/core/constants';
import { crm, type TaskRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TaskDialog } from '@/components/TaskDialog';
import { TaskStatusPill } from '@/components/TaskStatusPill';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';
import { useQuerySeededState } from '@/lib/useQuerySeededState';
import { cn } from '@/lib/utils';

/**
 * Alles, was zu erledigen ist — an einer Stelle.
 *
 * Auch die Aufgaben, die an einem Auftrag oder Projekt hängen und die, die
 * eine Automation angelegt hat. Sonst müsste man an zwei Orten nachsehen, um
 * die Frage „was ist heute zu tun“ zu beantworten.
 */
type Filter = 'aktiv' | TaskStatus;

const FILTER: { key: Filter; label: string }[] = [
  { key: 'aktiv', label: 'Alle offenen' },
  { key: 'offen', label: TASK_STATUS_LABELS.offen },
  { key: 'in_arbeit', label: TASK_STATUS_LABELS.in_arbeit },
  { key: 'erledigt', label: TASK_STATUS_LABELS.erledigt },
];

/** Der Tag, an dem etwas überfällig wird — heute 0 Uhr. */
function heuteBeginn(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function Faelligkeit({ dueAt }: { dueAt: string | null }) {
  if (!dueAt) return <span className="text-muted-foreground">—</span>;
  const ueberfaellig = dueAt < heuteBeginn();
  return (
    <span className={ueberfaellig ? 'font-medium text-danger' : 'text-muted-foreground'}>
      {new Date(dueAt).toLocaleDateString('de-DE')}
    </span>
  );
}

/** Wozu die Aufgabe gehört, sofern sie irgendwo herkommt. */
function Herkunft({ task }: { task: TaskRow }) {
  if (task.projectId) {
    return (
      <Link
        to={`/projekte/${task.projectId}`}
        state={{ from: '/aufgaben' }}
        className="text-brand hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        Projekt
      </Link>
    );
  }
  if (task.contactId) {
    return (
      <Link
        to={`/kontakte/${task.contactId}`}
        className="text-brand hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {task.contactName ?? 'Kontakt'}
      </Link>
    );
  }
  if (task.orderId) return <span className="text-muted-foreground">Auftrag</span>;
  return <span className="text-muted-foreground">—</span>;
}

export function TasksPage() {
  const [filter, setFilter] = useState<Filter>('aktiv');
  // Die globale Suche schickt ihren Begriff mit, weil Auftraege, Aufgaben und
  // Dateien keine Detailseite haben. Der Treffer landet damit auf seiner
  // Liste, vorgefiltert — statt auf einer ungefilterten mit dreihundert
  // Zeilen.
  const [suche, setSuche] = useQuerySeededState('q');
  const [fehler, setFehler] = useState<string | null>(null);

  const [schnell, setSchnell] = useState('');
  const [bearbeiten, setBearbeiten] = useState<TaskRow | 'neu' | null>(null);
  const [loeschen, setLoeschen] = useState<TaskRow | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    data: rowsData,
    loading,
    error: listenFehler,
  } = useDebouncedSearch<TaskRow[]>(
    () =>
      crm.tasks.list({
        ...(filter === 'aktiv' ? {} : { status: filter }),
        ...(suche.trim() ? { search: suche.trim() } : {}),
      }),
    [filter, suche, refreshKey],
  );
  const rows = rowsData ?? [];
  // `data === undefined` allein wuerde bei einem Fehlschlag fuer immer
  // "laedt" bleiben — die Anfrage kommt nie wieder, `data` bleibt leer.
  const ladeAufgaben = !listenFehler && (loading || rowsData === undefined);

  const schnellAnlegen = async () => {
    const name = schnell.trim();
    if (!name) return;
    setFehler(null);
    try {
      await crm.tasks.create({ title: name });
      setSchnell('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
    }
  };

  const weiterschalten = async (task: TaskRow, naechster: TaskStatus) => {
    setFehler(null);
    try {
      await crm.tasks.setStatus(task.id, naechster);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Wechsel fehlgeschlagen');
    }
  };

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            {FILTER.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  filter === f.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative min-w-[14rem]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Titel oder Notiz …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              aria-label="Aufgaben durchsuchen"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Input
              value={schnell}
              onChange={(e) => setSchnell(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void schnellAnlegen();
              }}
              placeholder="Neue Aufgabe … (Enter)"
              className="w-64"
            />
            <Button variant="outline" onClick={() => setBearbeiten('neu')}>
              <Plus className="h-4 w-4" />
              Mit Details
            </Button>
          </div>
        </div>
      </PageHeader>

      {(fehler ?? listenFehler) && (
        <p className="mb-3 text-sm text-danger">{fehler ?? listenFehler}</p>
      )}

      {ladeAufgaben ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {suche.trim()
            ? 'Keine Aufgabe passt zur Suche.'
            : filter === 'erledigt'
              ? 'Noch nichts erledigt.'
              : filter === 'in_arbeit'
                ? 'Nichts in Arbeit. Was du anfängst, landet hier.'
                : 'Keine offene Aufgabe. Automations und Auftrags-Checklisten legen hier ebenfalls welche ab.'}
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {rows.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-3 text-sm">
              <TaskStatusPill
                status={t.status}
                onWeiter={(naechster) => void weiterschalten(t, naechster)}
              />

              <span
                className={cn(
                  'min-w-0 flex-1 truncate',
                  t.status === 'erledigt' && 'text-muted-foreground line-through',
                )}
                title={t.notes ?? undefined}
              >
                {t.title}
              </span>

              <span className="w-40 shrink-0 truncate">
                <Herkunft task={t} />
              </span>
              <span className="w-24 shrink-0 text-right">
                <Faelligkeit dueAt={t.dueAt} />
              </span>

              <span className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t.title} bearbeiten`}
                  onClick={() => setBearbeiten(t)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t.title} löschen`}
                  onClick={() => setLoeschen(t)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </span>
            </div>
          ))}
        </Card>
      )}

      {!ladeAufgaben && rows.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'Aufgabe' : 'Aufgaben'}
        </p>
      )}

      {bearbeiten && (
        <TaskDialog
          {...(bearbeiten === 'neu' ? {} : { task: bearbeiten })}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {loeschen && (
        <ConfirmDialog
          title="Aufgabe löschen?"
          confirmLabel="Löschen"
          destructive
          onCancel={() => setLoeschen(null)}
          onConfirm={async () => {
            await crm.tasks.delete(loeschen.id);
            setLoeschen(null);
            setRefreshKey((k) => k + 1);
          }}
        >
          <p>
            „{loeschen.title}“ wird endgültig gelöscht. Aufgaben lassen sich nicht
            wiederherstellen.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
